import React from 'react'
import {
  createStyles,
  withStyles,
  Button,
  Dialog,
  DialogActions,
  DialogAfterContent,
  DialogContent,
  DialogTitle,
  IconButton
} from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses, DialogProps } from '@paintscout/ui'
import type { WithClientOptions, WithDialogStackContext } from '@paintscout/ui-business'
import { withClientOptions, withDialogStackContext } from '@paintscout/ui-business'
import type { QuoteDocument } from 'paintscout'
import CloseIcon from '@material-ui/icons/Close'
import type { QuoteItemSection, UpdateableArea, UpdateableLineItem } from '@paintscout/util/builder'
import { updateItem } from '@paintscout/util/builder'
import EditAreaDialog from '../EditAreaDialog'
import EditLineItemDialog from '../EditLineItemDialog'
import ItemConfiguration from '../../forms/ItemConfiguration'

export interface ItemConfigurationDialogProps extends DialogProps, WithDialogStackContext, WithClientOptions {
  classes?: DialogProps['classes'] & StyleClasses<typeof styles>
  quote: QuoteDocument
  section?: QuoteItemSection
  insertAfterKey?: string
  item?: UpdateableArea | UpdateableLineItem
  type?: any
  quoteTypes?: string[]
  onConfirm?: (quote: QuoteDocument) => void
  onCancel?: () => void
}

export interface ItemConfigurationDialogState {
  quote: QuoteDocument
  quoteTypes?: string[]
  error?: string
  title?: string
  item?: any
  type?: any
  section?: QuoteItemSection
}

const styles = (theme: Theme) => {
  return createStyles({
    root: {},
    dialogTitle: {
      display: 'grid',
      gridTemplateColumns: 'auto auto',
      alignItems: 'center',
      gridGap: theme.typography.pxToRem(theme.spacing())
    },
    dialogContent: {
      paddingTop: 0
    }
  })
}

class ItemConfigurationDialog extends React.PureComponent<ItemConfigurationDialogProps, ItemConfigurationDialogState> {
  public static defaultProps: Partial<ItemConfigurationDialogProps> = {
    insertAfterKey: null
  }

  constructor(props: ItemConfigurationDialogProps) {
    super(props)

    const { item, type, quote, quoteTypes, section } = this.props

    this.state = {
      quote,
      title: this.getTitle(type),
      error: '',
      item,
      type,
      quoteTypes,
      section
    }
  }

  public handleChange = (item: any, type: any) => {
    const { insertAfterKey } = this.props
    const { options } = this.props.clientOptions
    const { quote, section } = this.state
    if (!item && !type) {
      return
    } else if (!item) {
      this.setState({ title: this.getTitle(type.name), type })
      return
    }

    const updatedQuote = updateItem({ quote, options, item, insertAfterKey })

    const { dialogStackContext } = this.props
    if (item.type === 'area') {
      dialogStackContext.openDialog(EditAreaDialog, {
        quote: updatedQuote,
        item,
        isNew: true,
        consumer: 'customer',
        section,
        onConfirm: (newQuote: QuoteDocument) => {
          dialogStackContext.dismissAllDialogs()
          this.props.onConfirm(newQuote)
        },
        onCancel: () => {
          dialogStackContext.dismissDialog()
        }
      })
    } else if (item.type === 'lineItem') {
      dialogStackContext.openDialog(
        EditLineItemDialog,
        {
          quote: updatedQuote,
          item,
          isNew: true,
          consumer: 'customer',
          onConfirm: this.props.onConfirm,
          onCancel: () => {
            dialogStackContext.dismissDialog()
          }
        },
        { replace: true }
      )
    }
  }

  public handleTitleChange = (_event: any) => {
    this.setState({ item: null, type: null, title: this.getTitle(null) })
  }

  public getTitle = (type: string) => {
    if (type === 'lineItem') {
      return `Create Line Item`
    } else if (type === 'area') {
      return `Create Area`
    } else {
      return 'Create Area or Line Item'
    }
  }

  public render() {
    const {
      classes,
      loading,
      onCancel,
      onConfirm: _onConfirm,
      dialogStackContext: _dialogStackContext,
      insertAfterKey: _insertAfterKey,
      clientOptions: _clientOptions,
      ...otherProps
    } = this.props
    const { error, title: stateTitle, type, quote } = this.state

    const title = <div className={classes.dialogTitle}>{stateTitle}</div>

    return (
      <Dialog {...otherProps} className={classes.root} maxWidth={'md'} fullWidth={true}>
        <DialogTitle
          loading={loading}
          rightContent={
            <IconButton size="small" onClick={onCancel} disabled={loading}>
              <CloseIcon />
            </IconButton>
          }
        >
          {title}
        </DialogTitle>
        <DialogContent classes={{ root: classes.dialogContent }}>
          <ItemConfiguration
            onChange={this.handleChange}
            quote={quote}
            item={this.state.item}
            type={this.state.type}
            quoteTypes={this.state.quoteTypes}
            section={this.props.section}
          />
        </DialogContent>
        {error && <DialogAfterContent type={'error'}>{error}</DialogAfterContent>}
        {type && (
          <DialogActions
            leftButton={
              <Button variant="textNoPadding" onClick={this.handleTitleChange} disabled={loading}>
                Back
              </Button>
            }
          />
        )}
      </Dialog>
    )
  }
}

export default withStyles(styles)(withDialogStackContext(withClientOptions(ItemConfigurationDialog)))
